"use client";

import { TEvent } from "@/types/event";
import Image from "next/image";
import { Button } from "../ui/button";
import { TActivity } from "@/types/activity";
import { useEffect, useState } from "react";
import useBookingStore from "@/store/booking-store";
import { useRouter } from "next/navigation";
import { TSettings } from "@/types/settings";
import Link from "next/link";
import getEventActivity from "@/lib/get-event-activity";

export default function ActivitySidebar({
  activity,
  events,
  settings,
  onClose,
}: {
  activity: TActivity | null;
  events: TEvent[];
  settings: TSettings;
  onClose: () => void;
}) {
  const router = useRouter();
  const { booking, setBooking } = useBookingStore();

  const goToCheckout = (event?: TEvent) => {
    if (event) {
      setBooking({
        ...booking,
        event,
      });
    }

    router.push("/booking");
  };

  const [localEvents, setLocalEvents] = useState(events);
  const [crossEvents, setCrossEvents] = useState<TEvent[]>([]);

  useEffect(() => {
    const eve = events
      .filter(
        (e) =>
          e.extendedProps.activityId === activity?.id &&
          e.extendedProps.activityCategoryId ===
            activity?.activityCategories[0].id &&
          +e.start - (settings.latestCheckoutHours || 24) * 3600 * 1000 >
            +new Date() &&
          e.extendedProps.availableSlots > 0
      )
      .slice(0, 5);
    setLocalEvents(eve);

    setCrossEvents(
      events
        .filter(
          (e) =>
            e.extendedProps.activityCategoryId ===
              activity?.activityCategories[0].id &&
            e.extendedProps.activityId !== activity?.id &&
            +e.start - (settings.latestCheckoutHours || 24) * 3600 * 1000 >
              +new Date() &&
            e.extendedProps.availableSlots > 0 &&
            !eve.find((ev) => ev.id === e.id)
        )
        .slice(0, 5)
    );
  }, [events, activity]);

  return (
    <div className="p-4 radius-base relative">
      <Button
        className="absolute top-2 right-4 p-2 rounded-full bg-muted"
        variant="outline"
        onClick={() => onClose()}
      >
        X
      </Button>
      <h2
        className="text-base-text font-bold text-2xl font-domine border-b-2"
        style={{ borderColor: activity?.activityCategories[0].color }}
      >
        {activity?.displayTitleForCategory[0]}
      </h2>
      <div className="relative">
        {activity ? (
          <Image
            className="mt-3 w-full"
            width={600}
            height={448}
            src={activity.image || activity?.activityCategories[0].image || ""}
            alt={activity?.title || ""}
            unoptimized={true}
          />
        ) : (
          <></>
        )}
        <div className="absolute top-2 right-2 bg-secondary-text text-md font-bold text-muted pt-1 pb-1 pl-3 pr-3 rounded-full">
          {activity?.activityCategories[0].price}€
        </div>
      </div>
      {localEvents[0] ? (
        <>
          <div className="text-sm text-center mt-4 mb-4 font-bold">
            Próxima clase:{" "}
            {localEvents[0].start.toLocaleTimeString("es", {
              month: "long",
              day: "2-digit",
              hour: "2-digit",
              timeZone: "Europe/Madrid",
            })}
            -
            {localEvents[0].end.toLocaleTimeString("es", {
              hour: "2-digit",
              // minute: "2-digit",
              hour12: false,
              timeZone: "Europe/Madrid",
            })}
            h
          </div>
          <div className="flex justify-center m-2">
            <Button
              disabled={localEvents.length === 0}
              onClick={() =>
                localEvents.length > 0 && goToCheckout(localEvents[0])
              }
            >
              Reserva ahora
            </Button>
          </div>
        </>
      ) : (
        <></>
      )}
      <div
        className="text-sm p-2 mt-4"
        dangerouslySetInnerHTML={{
          __html: activity?.displayDescriptionForCategory[0] || "",
        }}
      ></div>
      <div className="mt-0 mb-4 p-4 text-center text-sm bg-quaternary-bg radius-base border-1 border-base-text border-dashed">
        ¿Quieres comprar una clase para un amigo?
        <Link
          title="Descubre nuestros vales regalo."
          href={"/coupon"}
          className="underline font-bold block mt-3"
        >
          Descubre nuestros vales regalo.
        </Link>
      </div>
      {localEvents.length > 0 ? (
        <div className="text-center mt-2 mb-4 font-bold text-2xl border-t-1 pt-4">
          Próximas clases:
        </div>
      ) : (
        <></>
      )}
      <div>
        {localEvents.map((event, key) => (
          <div
            key={event.id || key}
            className="flex flex-row items-center justify-between p-4 border-b-1"
          >
            <div>
              <div className="text-sm font-bold pt-2">
                <span>
                  {event?.start.toLocaleString("es", {
                    month: "long",
                    day: "2-digit",
                    // hour: "2-digit",
                    timeZone: "Europe/Madrid",
                  })}
                  <br />
                  {event?.start.toLocaleTimeString("es", {
                    hour: "2-digit",
                    timeZone: "Europe/Madrid",
                  })}
                  -
                  {event?.end.toLocaleTimeString("es", {
                    hour: "2-digit",
                    // minute: "2-digit",
                    hour12: false,
                    timeZone: "Europe/Madrid",
                  })}
                  h
                </span>
              </div>
              <div className="text-sm font-bolds">
                Plazas disponibles: {event?.extendedProps.availableSlots}
              </div>
            </div>
            <Button
              disabled={event.extendedProps.availableSlots === 0}
              onClick={() => goToCheckout(event)}
            >
              Reserva ahora
            </Button>
          </div>
        ))}
      </div>

      {crossEvents.length > 0 ? (
        <>
        <div className="mt-8 mb-4 p-4 text-center text-sm bg-quaternary-bg radius-base border-1 border-base-text border-dashed">
          ¿Buscas una clase más adelante?
          <Link
            title="Descubre todas nuestras clases en
          nuestro calendario."
            href={"/"}
            className="underline font-bold block mt-3"
          >
            Descubre todas nuestras clases en nuestro calendario.
          </Link>
        </div>
        <div className="text-center mt-2 mb-4 font-bold text-2xl border-t-1 pt-4">
          También te puede interesar:
        </div>
        </>
      ) : (
        <></>
      )}
      <div>
        {crossEvents.map((event, key) => (
          <div
            key={event.id || key}
            className="flex flex-row items-center justify-between p-4 border-b-1"
          >
            <div>
              <div className="text-sm font-bolda pt-2">
                <span>
                  {event?.start.toLocaleString("es", {
                    month: "long",
                    day: "2-digit",
                    // hour: "2-digit",
                    timeZone: "Europe/Madrid",
                  })}
                  <br />
                  {event?.start.toLocaleTimeString("es", {
                    hour: "2-digit",
                    timeZone: "Europe/Madrid",
                  })}
                  -
                  {event?.end.toLocaleTimeString("es", {
                    hour: "2-digit",
                    // minute: "2-digit",
                    hour12: false,
                    timeZone: "Europe/Madrid",
                  })}
                  h
                </span>
              </div>
              <div className="text-sm font-bold inline border-b-primary-text border-b-2">
                {getEventActivity(event)?.displayTitleForCategory[0]}
              </div>
              <div className="text-sm font-bolds">
                Plazas disponibles: {event?.extendedProps.availableSlots}
              </div>
            </div>
            <Button
              disabled={event.extendedProps.availableSlots === 0}
              onClick={() => goToCheckout(event)}
            >
              Reserva ahora
            </Button>
          </div>
        ))}
        <div className="mt-8 mb-4 p-4 text-center text-sm bg-quaternary-bg radius-base border-1 border-base-text border-dashed">
          ¿Buscas una clase más adelante?
          <Link
            title="Descubre todas nuestras clases en
          nuestro calendario."
            href={"/"}
            className="underline font-bold block mt-3"
          >
            Descubre todas nuestras clases en nuestro calendario.
          </Link>
        </div>
      </div>
    </div>
  );
}
